import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Badge, Alert, Spinner } from 'react-bootstrap';
import { getGoogleSpreadsheetData } from '../services/api';
import './ParameterImputationModal.css';

const ParameterImputationModal = ({ 
  show, 
  onHide, 
  googleSheetsData, 
  projectData,
  googleSheetsInfo, // Add googleSheetsInfo prop
  onStartInterpolation,
  loading: externalLoading = false, // Add loading prop from parent
  error: externalError = '' // Add error prop from parent
}) => {const [availableColumns, setAvailableColumns] = useState([]);
  const [columnStats, setColumnStats] = useState({}); // Store missing value counts for each column
  const [mappings, setMappings] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Interpolation methods
  const interpolationMethods = [
    { id: 'linear', label: 'Linear Interpolation', color: 'primary' },
    { id: 'polynomial', label: 'Polynomial', color: 'success' },
    { id: 'spline', label: 'Spline', color: 'info' },
    { id: 'forward_fill', label: 'Forward Fill', color: 'warning' },
    { id: 'backward_fill', label: 'Backward Fill', color: 'danger' },
    { id: 'mean', label: 'Mean', color: 'secondary' },
    { id: 'median', label: 'Median', color: 'dark' },
    { id: 'mode', label: 'Mode', color: 'light' }  ];

  const extractAvailableColumns = useCallback(async () => {
    try {
      setLoading(true);
      
      if (!googleSheetsInfo?.spreadsheet_id) {
        setAvailableColumns([]);
        setColumnStats({});
        return;
      }

      // Fetch fresh data from Google Sheets
      const response = await getGoogleSpreadsheetData(googleSheetsInfo.spreadsheet_id);
      const data = response.data || [];
      
      if (data.length > 0 && data[0]) {
        // Extract column names from the first row and exclude first 4 columns
        const allColumns = data[0]; // First row contains column headers
        const relevantColumns = allColumns.slice(4); // Skip first 4 columns
        
        console.log('All columns:', allColumns);
        console.log('Relevant columns (excluding first 4):', relevantColumns);
        
        // Filter out empty column names
        const validColumns = relevantColumns.filter(col => col && col.trim() !== '');
        
        // Analyze each column for missing values
        const stats = {};
        const columnsWithMissingData = [];
        
        validColumns.forEach((column, columnIndex) => {
          const actualColumnIndex = columnIndex + 4; // Adjust for skipped columns
          let missingCount = 0;
          let totalCount = 0;
          
          // Check each row for this column (skip header row)
          for (let rowIndex = 1; rowIndex < data.length; rowIndex++) {
            const row = data[rowIndex];
            if (row && actualColumnIndex < row.length) {
              const cellValue = row[actualColumnIndex];
              totalCount++;
              
              // Check if value is missing, empty, NA, or NaN
              if (cellValue === null || 
                  cellValue === undefined || 
                  cellValue === '' || 
                  cellValue === 'NA' || 
                  cellValue === 'NaN' || 
                  cellValue === 'N/A' ||
                  cellValue === '#N/A' ||
                  (typeof cellValue === 'string' && cellValue.trim() === '') ||
                  (typeof cellValue === 'number' && isNaN(cellValue))) {
                missingCount++;
              }
            }
          }
          
          stats[column] = {
            missingCount,
            totalCount,
            hasMissingData: missingCount > 0
          };
          
          // Only include columns that have missing data
          if (missingCount > 0) {
            columnsWithMissingData.push(column);
          }
        });
        
        setAvailableColumns(columnsWithMissingData);
        setColumnStats(stats);
        
        console.log('Column statistics:', stats);
        console.log('Columns with missing data:', columnsWithMissingData);
      } else {
        setAvailableColumns([]);
        setColumnStats({});
      }
    } catch (error) {
      console.error('Error extracting columns:', error);
      setAvailableColumns([]);
      setColumnStats({});
    } finally {
      setLoading(false);
    }
  }, [googleSheetsInfo]);

  useEffect(() => {
    if (show && googleSheetsInfo) {
      extractAvailableColumns();
    }
    
    // Cleanup when modal is hidden
    if (!show) {
      setAvailableColumns([]);
      setColumnStats({});
      setMappings({});
      setLoading(false);
      setError('');
    }
  }, [show, googleSheetsInfo, extractAvailableColumns]);

  const handleMappingChange = (column, method) => {
    setMappings(prev => ({
      ...prev,
      [column]: method
    }));
  };
  const handleStartInterpolation = () => {
    const selectedMappings = Object.entries(mappings).filter(([col, method]) => method !== '');
    
    if (selectedMappings.length === 0) {
      setError('Please select at least one interpolation method for a column');
      return;
    }

    // Clear local error before calling parent function
    setError('');

    if (onStartInterpolation) {
      onStartInterpolation(mappings, availableColumns);
    }
    
    // Don't close modal here - let parent handle it based on success/failure
  };
  const getMethodBadgeColor = (methodId) => {
    const method = interpolationMethods.find(m => m.id === methodId);
    return method ? method.color : 'secondary';
  };

  // Helper function to get icon for each interpolation method
  const getMethodIcon = (methodId) => {
    const icons = {
      'linear': '📈',
      'polynomial': '🔢',
      'spline': '〰️',
      'forward_fill': '➡️',
      'backward_fill': '⬅️',
      'mean': '📊',
      'median': '📐',
      'mode': '🔄'
    };
    return icons[methodId] || '❓';
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      centered
      className="parameter-imputation-modal"
    >
      <Modal.Header closeButton className="parameter-imputation-header">
        <Modal.Title>
          <span className="modal-icon">🔧</span>
          Parameters Imputation
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="parameter-imputation-body">        {loading && (
          <div className="text-center p-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <div className="mt-2">Analyzing data columns for missing values...</div>
          </div>
        )}        {(error || externalError) && (
          <Alert variant="danger" className="mb-3">
            {externalError || error}
          </Alert>
        )}

        {!loading && !externalLoading && !error && !externalError && (
          <>
            {/* Interpolation Methods Legend */}
            <div className="interpolation-methods mb-4">
              <h6 className="mb-3">Available Interpolation Methods:</h6>
              <div className="methods-grid">
                {interpolationMethods.map(method => (
                  <Badge 
                    key={method.id}
                    bg={method.color}
                    className="method-badge"
                  >
                    {method.label}
                  </Badge>
                ))}
              </div>
            </div>            {/* Column Mappings */}
            <div className="column-mappings">
              <h6 className="mb-3">
                Map Interpolation Methods to Columns 
                <span className="text-muted">({availableColumns.length} columns with missing data found)</span>
              </h6>
              
              {availableColumns.length === 0 ? (
                <Alert variant="success">
                  <div className="d-flex align-items-center">
                    <span className="me-2">✅</span>
                    <div>
                      <strong>Great! No missing data found.</strong>
                      <div className="text-muted mt-1">
                        All columns in your data appear to be complete. No interpolation is needed.
                      </div>
                    </div>
                  </div>
                </Alert>
              ) : (
                <div className="columns-list">
                  {availableColumns.map((column, index) => {
                    const stats = columnStats[column] || {};
                    return (
                      <div key={column} className="column-card mb-4 p-3 border rounded">                        <div className="column-header mb-3">
                          <div className="d-flex align-items-center flex-wrap gap-2">
                            <Badge bg="secondary" className="column-badge fs-6">
                              {column}
                            </Badge>
                            <Badge bg="warning" className="missing-data-badge">
                              {stats.missingCount} missing of {stats.totalCount} values
                            </Badge>
                            {mappings[column] && (
                              <Badge 
                                bg={getMethodBadgeColor(mappings[column])}
                                className="selected-method-badge"
                              >
                                Selected: {interpolationMethods.find(m => m.id === mappings[column])?.label}
                              </Badge>
                            )}
                          </div>
                          
                          {stats.missingCount > 0 && (
                            <div className="missing-data-info mt-2">
                              <small className="text-muted">
                                📊 Missing data ratio: {((stats.missingCount / stats.totalCount) * 100).toFixed(1)}%
                              </small>
                            </div>
                          )}
                        </div>
                        
                        <div className="method-selection-grid">
                          <div className="methods-row">
                            {interpolationMethods.map(method => (
                              <button
                                key={method.id}
                                type="button"
                                className={`method-card btn ${mappings[column] === method.id ? 'selected' : ''}`}
                                onClick={() => handleMappingChange(column, method.id)}
                                data-method-color={method.color}
                              >
                                <div className="method-icon">
                                  {getMethodIcon(method.id)}
                                </div>
                                <div className="method-label">
                                  {method.label}
                                </div>
                              </button>
                            ))}
                          </div>
                          
                          {mappings[column] && (
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm mt-2 clear-selection-btn"
                              onClick={() => handleMappingChange(column, '')}
                            >
                              Clear Selection
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>            {/* Summary */}
            {Object.values(mappings).some(method => method !== '') && (
              <div className="mapping-summary mt-4 p-3 bg-light rounded">
                <h6>Selected Mappings:</h6>
                <div className="summary-list">
                  {Object.entries(mappings)
                    .filter(([col, method]) => method !== '')
                    .map(([column, method]) => (
                      <div key={column} className="summary-item">
                        <Badge bg="secondary" className="column-name-badge me-2">{column}</Badge>
                        <span className="text-muted mapping-arrow">→</span>
                        <Badge 
                          bg={getMethodBadgeColor(method)}
                          className="method-name-badge ms-2"
                        >
                          {interpolationMethods.find(m => m.id === method)?.label}
                        </Badge>
                      </div>
                    ))
                  }
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      
      <Modal.Footer className="parameter-imputation-footer">
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>        <Button 
          variant="primary"
          onClick={handleStartInterpolation}
          disabled={loading || externalLoading || Object.values(mappings).every(method => method === '')}
          className="start-interpolation-btn"
        >
          {externalLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Processing...
            </>
          ) : (
            <>
              <span className="btn-icon">🚀</span>
              Start Interpolation
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ParameterImputationModal;
